/**
 * Narrowing a community's roster: a search box and a row of tags.
 *
 * A COMMUNITY PAGE IS MOSTLY OFFLINE PEOPLE, which is what makes filtering
 * worth having at all. A roster of fifty with three live is the normal
 * evening, so the page's job is to help somebody find a person — not to rank
 * whoever happens to be streaming.
 *
 * Kept out of the component so the rules can be tested without rendering
 * anything, and because getting them subtly wrong is quiet: a filter that
 * matches nothing looks exactly like a community with nobody in it.
 */

/**
 * The WIRE shape, snake_case, because that is what the console holds.
 *
 * `@tomo/contracts` publishes snake_case and the stored side is camelCase;
 * translating here would make this the second place that knows both
 * spellings, which is the one rule that has already cost three silent
 * failures in deployed code. See docs/architecture/casing.md.
 */
export interface Filterable {
  readonly login: string;
  readonly display_name: string;
  readonly description: string;
  readonly tags: readonly string[];
  /** Present or not is all this needs; the card reads the detail. */
  readonly live: object | null;
}

/**
 * Does this person match what was typed?
 *
 * Matches the LOGIN, the DISPLAY NAME and the DESCRIPTION, because all three
 * are how somebody is actually looked for. A viewer remembers "the one who
 * plays roguelikes" more often than a login, and a display name can differ
 * from the login entirely — `TognaZambogna` against `tognazambogna` is
 * benign, but plenty of people use a name with no relation to their handle.
 */
export function matchesTerm(member: Filterable, term: string): boolean {
  const needle = term.trim().toLowerCase();
  if (needle.length === 0) return true;
  return (
    member.login.toLowerCase().includes(needle) ||
    member.display_name.toLowerCase().includes(needle) ||
    member.description.toLowerCase().includes(needle)
  );
}

/** Does this person carry the chosen tag? `null` is "All". */
export function matchesTag(member: Filterable, tag: string | null): boolean {
  return tag === null || member.tags.includes(tag);
}

export function filterMembers<T extends Filterable>(
  members: readonly T[],
  term: string,
  tag: string | null,
): T[] {
  return members.filter((m) => matchesTerm(m, term) && matchesTag(m, tag));
}

/**
 * Every tag on the roster, commonest first, with ties broken alphabetically.
 *
 * FROM THE WHOLE ROSTER, not from whoever is live. That is the difference
 * between a filter worth having and one that is empty on a quiet night, and
 * it is only possible because the tags come from each broadcaster's channel
 * rather than from their current stream.
 *
 * Ties are broken by name so the row does not reshuffle itself every time
 * somebody goes live, which would move a button out from under a cursor.
 */
/**
 * How many tags the row shows before it stops being a row.
 *
 * Measured against the real 567: scraping Twitch gives 55 members some two
 * hundred distinct tags, and drawn in full they filled the entire first
 * screen and pushed every streamer below the fold. The filter had become the
 * page.
 */
export const TAG_ROW_LIMIT = 16;

/**
 * The tags worth offering as buttons.
 *
 * A TAG ONE PERSON CARRIES IS NOT A FILTER. Pressing it narrows fifty-five
 * people to one, which is what the search box already does, and better —
 * you can type a name but you cannot type a button that is not on screen.
 * So the row is the tags that actually group somebody, commonest first, and
 * the long tail stays reachable through search rather than as two hundred
 * buttons nobody will read.
 */
export function rowTags(
  members: readonly Filterable[],
  limit = TAG_ROW_LIMIT,
): { tag: string; count: number }[] {
  return tagsOf(members).filter((t) => t.count > 1).slice(0, limit);
}

export function tagsOf(members: readonly Filterable[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const member of members) {
    for (const tag of member.tags) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag));
}

/**
 * Split into the two sections the page draws, preserving order.
 *
 * The server has already ordered the roster — live first, longest uptime
 * first, then alphabetical — so this only cuts it in two. Re-sorting here
 * would be a second copy of a rule that lives in one place on purpose.
 */
export function splitByLive<T extends Filterable>(
  members: readonly T[],
): { live: T[]; offline: T[] } {
  return {
    live: members.filter((m) => m.live !== null),
    offline: members.filter((m) => m.live === null),
  };
}

/** "3 streamers live now", and the one case where it is not a plural. */
export function liveHeadline(count: number): string {
  if (count === 0) return 'Nobody is live right now';
  return `${String(count)} streamer${count === 1 ? '' : 's'} live now`;
}

/**
 * Where "Watch together" goes.
 *
 * The community's own room, with the picks named in the address. The rooms
 * app reads `?channels=` and opens each one FULL with the first audible
 * (`openingSettings`), which is what makes a selection on this page arrive as
 * a grid rather than as a list somebody then has to build again.
 *
 * With nothing picked it is the bare room, which opens whatever that
 * community's arrival rules choose. Picking nothing is a legitimate way to
 * say "surprise me", not an error to block the button for.
 */
export function watchHref(slug: string, selection: readonly string[]): string {
  const room = `/c/${encodeURIComponent(slug)}`;
  if (selection.length === 0) return room;
  return `${room}?channels=${encodeURIComponent(selection.join(','))}`;
}

/** Somebody's Twitch page. The card links out as well as in. */
export function twitchHref(login: string): string {
  return `https://twitch.tv/${encodeURIComponent(login)}`;
}

/**
 * Everybody by name, for the "Live first" switch turned off.
 *
 * Display name, ignoring case, so `cll0verr` does not sort after every
 * capitalised name; the login breaks ties between two people who chose the
 * same one.
 */
export function byName<T extends Pick<Filterable, 'login' | 'display_name'>>(members: readonly T[]): T[] {
  return [...members].sort((a, b) =>
    a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' })
    || a.login.localeCompare(b.login));
}

/** Where the switch is remembered. A convenience per browser, never state anybody else reads. */
const LIVE_FIRST_KEY = 'tomo.community.liveFirst';

/** On unless this browser turned it off. Storage that throws (private mode) reads as on. */
export function readLiveFirst(storage: Pick<Storage, 'getItem'> | null): boolean {
  try {
    return storage?.getItem(LIVE_FIRST_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function saveLiveFirst(storage: Pick<Storage, 'setItem'> | null, on: boolean): void {
  try {
    storage?.setItem(LIVE_FIRST_KEY, on ? 'on' : 'off');
  } catch {
    // Remembering is a nicety; failing to is not worth telling anybody about.
  }
}
